import { Component, OnInit, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { DateFormatPipe, DynamicHeight } from '../../helper';
import { IBusiness, ICampaignScreen } from '../../models';
import { CampaignService, CommonService } from '../../services';
import { BusinessService } from '../../services/business';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BusinessQuickViewComponent } from './business-quick-view/business-quick-view.component';
import { ScreenQuickViewComponent } from './screen-quick-view/screen-quick-view.component';
import { Router } from '@angular/router';
import { Chart } from 'chart.js/auto';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    DateFormatPipe,
    DynamicHeight,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule
  ],
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, AfterViewInit {

  businesses: IBusiness[] = [];

  total = 0;
  active = 0;
  trial = 0;
  expired = 0;

  selectedRange = 7;

  expiryOptions = [
    { label: 'Next 7 Days', value: 7 },
    { label: 'Next 30 Days', value: 30 },
    { label: 'Next 6 Months', value: 180 },
    { label: 'Next 1 Year', value: 365 }
  ];

  expiringSoon: IBusiness[] = [];
  recentBusinesses: IBusiness[] = [];
  screens: ICampaignScreen[] = [];

  totalScreens = 0;
  liveScreens = 0;
  standbyScreens = 0;
  inactiveScreens = 0;
  allScreens: ICampaignScreen[] = [];

  chart: any;

  constructor(
    private businessService: BusinessService,
    private campaignService: CampaignService,
    public commonService: CommonService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadDashboard();
    this.loadScreens();
  }

  ngAfterViewInit(): void {
    // Wait for data also
    setTimeout(() => {
      this.createChart();
    }, 0);
  }

  loadDashboard() {
    this.commonService.showSpinner = true;

    this.businessService.getAllBusiness().subscribe({
      next: (res) => {
        this.businesses = res?.data?.results || [];

        this.calculateStats();
        this.prepareTables();

        this.createChart();

        this.commonService.showSpinner = false;
      },
      error: () => {
        this.commonService.showSpinner = false;
      }
    });
  }

  loadScreens() {
    this.campaignService.getAllBusinessScreens().subscribe({
      next: (res: any[]) => {
        this.calculateScreenStatsFromApi(res);
      }
    });
  }

  calculateStats() {
    const today = new Date();

    this.total = this.businesses.length;

    this.active = this.businesses.filter(b => b.status === 'live').length;
    this.trial = this.businesses.filter(b => b.status === 'trial').length;

    this.expired = this.businesses.filter(b =>
      new Date(b.expiry_date) < today
    ).length;
  }

  calculateScreenStatsFromApi(data: any[]) {
    let live = 0;
    let standby = 0;
    let inactive = 0;
    const all: ICampaignScreen[] = [];
    console.log(data);
    data.forEach(business => {
      business.screens.forEach((screen: any) => {
        const status = (screen.status || '').toLowerCase();
        if (status === 'live') live++;
        else if (status === 'standby') standby++;
        else inactive++;
        all.push({
          screen_id: screen.screen_id,
          screen_name: screen.screen_name,
          status: screen.status,
          filler_ad: screen.is_admin_ad_enabled
        } as ICampaignScreen);
      });
    });

    this.liveScreens = live;
    this.standbyScreens = standby;
    this.inactiveScreens = inactive;
    this.totalScreens = live + standby + inactive;
    this.allScreens = all;
  }

  prepareTables() {
    const today = new Date();

    this.expiringSoon = this.businesses
      .filter(b => {
        const diff = this.getDiffDays(b.expiry_date);
        return diff >= 0 && diff <= this.selectedRange;
      })
      .sort((a, b) =>
        new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime()
      );

    this.recentBusinesses = this.businesses.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    // .slice(0, 5);
  }

  getDiffDays(date: string) {
    const today = new Date();
    const expiry = new Date(date);

    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  goToBusinessList() {
    this.router.navigate(['/business']);
  }

  openScreenQuickView(title: string, screens: ICampaignScreen[]) {
    this.dialog.open(ScreenQuickViewComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '80vh',
      data: {
        title,
        screens
      }
    });
  }

  openLiveScreens() {
    const screens = this.allScreens.filter(s => (s.status || '').toLowerCase() === 'live');
    this.openScreenQuickView('Live Screens', screens);
  }

  openStandbyScreens() {
    const screens = this.allScreens.filter(s => (s.status || '').toLowerCase() === 'standby');
    this.openScreenQuickView('Standby Screens', screens);
  }

  openInactiveScreens() {
    const screens = this.allScreens.filter(s => (s.status || '').toLowerCase() === 'inactive');
    this.openScreenQuickView('Inactive Screens', screens);
  }

  openAllScreens() {
    this.openScreenQuickView('All Screens', this.allScreens);
  }

  refreshDashboard() {
    this.loadDashboard();
    this.loadScreens();
  }

  updateExpiryData() {
    this.prepareTables();
  }


  openTrialBusinesses() {
    const trialBusinesses = this.businesses.filter(b => b.status === 'trial');
    this.dialog.open(BusinessQuickViewComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '80vh',
      data: {
        title: 'Trial Businesses',
        businesses: trialBusinesses
      }
    });
  }

  openExpiredBusinesses() {
    const today = new Date();
    const expired = this.businesses.filter(b => new Date(b.expiry_date) < today);
    this.dialog.open(BusinessQuickViewComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '80vh',
      data: {
        title: 'Expired Businesses',
        businesses: expired
      }
    });
  }


  createChart() {
    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart('businessChart', {
      type: 'doughnut', // 🔥 change to 'bar' or 'line' if needed
      data: {
        labels: ['Active', 'Trial', 'Expired'],
        datasets: [{
          data: [this.active, this.trial, this.expired],
          backgroundColor: [
            '#28a745',
            '#ffc107',
            '#dc3545'
          ]
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    });
  }

  getTableHeight(): string {
    return window.innerHeight - 510 + 'px';
  }
}
